import React, { useState } from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiAlertCircle, FiInfo, FiCheckCircle } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { NotificationPriority, Notification } from '../../components/notification-bell';
import { useNotifications } from '../../store/useNotifications';
import { obterIdTenent } from '../../services/seguranca';
import { useAppSelector } from '../../store/hooks';
import { LocaSmartLayout } from '../../components/locasmart-layout';
import { formatTimeAgo } from '../../services/utilitarios';

const Container = styled.div`
  padding: 24px 32px;
  width: 100%;
  max-width: 1200px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #1890ff;
  font-size: 15px;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(24, 144, 255, 0.08);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
`;

const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const NotificationItem = styled.div<{ priority: NotificationPriority }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 10px;
  background: #fff;
  border-left: 5px solid
    ${props =>
      props.priority === 'high'
        ? '#ff4d4f'
        : props.priority === 'medium'
        ? '#faad14'
        : '#52c41a'};
  box-shadow: 0 3px 8px rgba(0,0,0,0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
`;

const IconWrapper = styled.div<{ priority: NotificationPriority }>`
  flex-shrink: 0;
  font-size: 22px;
  margin-top: 3px;
  color: ${props =>
    props.priority === 'high'
      ? '#ff4d4f'
      : props.priority === 'medium'
      ? '#faad14'
      : '#52c41a'};
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h2`
  margin: 0 0 6px 0;
  font-size: 17px;
  font-weight: 600;
  color: #222;
`;

const NotificationDescription = styled.p`
  margin: 0;
  color: #555;
  font-size: 15px;
  line-height: 1.5;
`;

const NotificationTime = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: #999;
`;

const DetailContent = styled.div`
  margin-top: 8px;
  padding: 10px;
  background: #fafafa;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  line-height: 1.5;
  color: #555;
  white-space: pre-line;
`;

interface NotificationsPageProps {
  onBack?: () => void;
  onNotificationClick?: (notification: Notification) => void;
}

export const NotificationsPage: React.FC<NotificationsPageProps> = ({
  onBack,
  onNotificationClick
}) => {
  const [token] = useState<string>(useAppSelector(state => state.token));
  const { notifications } = useNotifications(obterIdTenent(token));
  const navigate = useHistory();
  const history = useHistory();

  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    if(notification.url) {
      const targetUrl = new URL(notification.url, window.location.origin);
      const currentPath = window.location.pathname;
      const targetPath = targetUrl.pathname;
    
      if (currentPath === targetPath) {
        window.location.href = notification.url;
      } else {
        history.push(notification.url);
      }
    }
  };

  const getIconByPriority = (priority: NotificationPriority) => {
    switch (priority) {
      case 'high':
        return <FiAlertCircle />;
      case 'medium':
        return <FiInfo />;
      default:
        return <FiCheckCircle />;
    }
  };

  return (
    <LocaSmartLayout>
      <Container>
        <Header>
          <BackButton onClick={() => (onBack ? onBack() : navigate.goBack())}>
            <FiArrowLeft /> Voltar
          </BackButton>
          <Title>Central de Notificações</Title>
        </Header>

        <NotificationList>
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              priority={notification.priority}
              onClick={() => handleNotificationClick(notification)}
            >
              <IconWrapper priority={notification.priority}>
                {getIconByPriority(notification.priority)}
              </IconWrapper>

              <NotificationContent>
                <NotificationTitle>{notification.title}</NotificationTitle>
                <NotificationDescription>{notification.description}</NotificationDescription>
                {notification.date && (
                  <NotificationTime>
                    {formatTimeAgo(notification.date)}
                  </NotificationTime>
                )}
                {notification.details && (
                  <DetailContent>{notification.details}</DetailContent>
                )}
              </NotificationContent>
            </NotificationItem>
          ))}
        </NotificationList>
      </Container>
    </LocaSmartLayout>
  );
};